import React from "react";
import type { BibleBook, BibleFootnote } from "./bible";

export type FootnoteData = {
  _id?: string;
  seq: string;
  note: string;
  verse: string;
  location: number;
  flag?: string;
};

export type VerseRef = { bookId: string; chapter: number; verse: string };

export type AnnRef = {
  bookId: string;
  chapter: number;
  verse: string;
  seq: string;
  endVerse?: string;
  flag?: string;
  sourceFootnoteId?: string;
  sourceLocation?: number;
  label?: string;
};

export type VerseRangeItem = {
  bookId: string;
  bookName: string;
  chapterStart: number;
  verseStart: number;
  chapterEnd: number;
  verseEnd: number;
  flagStart?: string;
  flagEnd?: string;
};

export type GroupedParenEntry =
  | { id: string; label: string; kind: "verse"; bookId: string; chapter: number; verse: string }
  | { id: string; label: string; kind: "ann"; annRef: AnnRef }
  | { id: string; label: string; kind: "range"; bookId: string; verseRanges: VerseRangeItem[]; annRef?: AnnRef };

export type ModalDescriptor = {
  id: string;
  bookId: string;
  chapter?: number;
  footnotes?: FootnoteData[];
  verseRef?: VerseRef;
  annRef?: AnnRef;
  verseRanges?: VerseRangeItem[];
  rangeLabel?: string;
  groupedParenEntries?: GroupedParenEntry[];
  groupedParenLabel?: string;
};

export interface RefParseContext {
  books: BibleBook[];
  getBookName: (bookId: string) => string;
  pushModal: (m: ModalDescriptor) => void;
  nextModalId: { current: number };
  curBookId: string;
  curChapter: number;
  curVerse?: string;
  curFlag?: string;
  sourceFootnoteId?: string;
  sourceLocation?: number;
}

// 中文数字 → 阿拉伯数字（含 二八→28、三五→35 等十进制缩写与 十/百 标准形式）
export const chineseNumToInt = (s: string): number | null => {
  const m: Record<string, number> = { '一': 1, '二': 2, '三': 3, '四': 4, '五': 5, '六': 6, '七': 7, '八': 8, '九': 9, '零': 0, '○': 0, '十': 10, '百': 100 };
  if (!s) return null;
  if (s.length === 1) return m[s] ?? null;
  const hasPlace = s.includes("十") || s.includes("百");
  if (!hasPlace) {
    let result = 0;
    for (const ch of s) {
      const n = m[ch];
      if (n === undefined || n >= 10) return null;
      result = result * 10 + n;
    }
    return result || null;
  }
  let r = 0;
  let t = 0;
  for (const ch of s) {
    const n = m[ch];
    if (n === undefined) return null;
    if (n >= 10) {
      t = (t || 1) * n;
      r += t;
      t = 0;
    } else {
      t = n;
    }
  }
  r += t;
  return r || null;
};

export const hasRenderableFootnoteNote = (note: string | undefined): boolean =>
  typeof note === "string" && note.trim().length > 0;

// 把书卷 id 解析为当前版本可匹配 footnotes.book_index / verse.Book 的引用集合
export const getBookRefsForVersion = (books: BibleBook[], bookId: string): string[] => {
  const book = books.find((b) => b._id === bookId) || books.find((b) => b.book_id === bookId);
  if (!book) return [bookId];
  return Array.from(new Set([book._id, book.book_id, book.name, book.acronym_name].filter(Boolean)));
};

export const getFootnotes = (
  footnotes: BibleFootnote[],
  books: BibleBook[],
  bookId: string,
  chapter: number,
  verse: string,
  verseFlag: string
): FootnoteData[] => {
  if (!footnotes || footnotes.length === 0) return [];
  const refs = getBookRefsForVersion(books, bookId);
  const list = footnotes
    .filter(
      (f) =>
        refs.includes(String(f.book_index)) &&
        String(f.chapter) === String(chapter) &&
        String(f.section) === String(verse) &&
        String(f.flag) === String(verseFlag)
    )
    .map((f) => ({
      _id: f._id,
      seq: f.seq,
      note: f.note,
      location: parseInt(f.location, 10) || 0,
      verse,
      flag: verseFlag,
    }))
    .sort((a, b) => a.location - b.location);
  const noteBySeq = new Map<string, string>();
  list.forEach((fn) => {
    if (!noteBySeq.has(String(fn.seq)) && hasRenderableFootnoteNote(fn.note)) {
      noteBySeq.set(String(fn.seq), fn.note);
    }
  });
  return list.map((fn) => {
    if (hasRenderableFootnoteNote(fn.note)) return fn;
    const fallbackNote = noteBySeq.get(String(fn.seq));
    return fallbackNote ? { ...fn, note: fallbackNote } : fn;
  });
};

export const getAnnotationFootnote = (
  footnotes: BibleFootnote[],
  books: BibleBook[],
  bookId: string,
  chapter: number,
  verse: string,
  seq: string,
  preferredFlag?: string,
  sourceFootnoteId?: string,
  sourceLocation?: number,
  endVerse?: string
): BibleFootnote | null => {
  if (!footnotes || footnotes.length === 0) return null;
  const refs = getBookRefsForVersion(books, bookId);
  const baseCandidates = footnotes.filter(
    (f) =>
      refs.includes(String(f.book_index)) &&
      String(f.chapter) === String(chapter) &&
      String(f.section) === String(verse) &&
      String(f.seq) === String(seq)
  );
  const fallbackCandidates =
    endVerse != null && String(endVerse) !== String(verse)
      ? footnotes.filter(
          (f) =>
            refs.includes(String(f.book_index)) &&
            String(f.chapter) === String(chapter) &&
            String(f.section) === String(endVerse) &&
            String(f.seq) === String(seq)
        )
      : [];
  const candidates = baseCandidates.length > 0 ? baseCandidates : fallbackCandidates;
  const sourceMatch =
    candidates.find((f) => sourceFootnoteId != null && f._id === sourceFootnoteId) ||
    candidates.find((f) => sourceLocation != null && (parseInt(f.location, 10) || 0) === sourceLocation);
  const preferredCandidates =
    preferredFlag == null
      ? candidates
      : candidates.filter((f) => String(f.flag || "0") === String(preferredFlag));
  return (
    (sourceMatch && hasRenderableFootnoteNote(sourceMatch.note) ? sourceMatch : null) ||
    preferredCandidates.find((f) => hasRenderableFootnoteNote(f.note)) ||
    candidates.find((f) => hasRenderableFootnoteNote(f.note)) ||
    sourceMatch ||
    preferredCandidates[0] ||
    candidates[0] ||
    null
  );
};

// 恢复版分段合并后的注解定位（与 holybible mergeRecoveryVerseFootnotes 一致）
export const mergeRecoveryVerseFootnotes = (
  parts: { Flag?: string; Verse: string; Text: string }[],
  footnotes: BibleFootnote[],
  books: BibleBook[],
  bookId: string,
  chapter: number
): FootnoteData[] => {
  const merged: FootnoteData[] = [];
  let textOffset = 0;
  parts.forEach((part) => {
    const partFlag = String(part.Flag || "0");
    const fns = getFootnotes(footnotes, books, bookId, chapter, String(part.Verse), partFlag);
    fns.forEach((fn) => {
      merged.push({
        ...fn,
        location: textOffset + (fn.location || 0),
        verse: String(part.Verse),
        flag: partFlag,
      });
    });
    textOffset += String(part.Text || "").length + 1;
  });
  return merged.sort((a, b) => a.location - b.location);
};

export const getVerseFlagSortOrder = (flag?: string): number => {
  switch (String(flag || "0")) {
    case "0":
      return 0;
    case "1":
      return 1;
    case "3":
      return 2;
    case "2":
      return 3;
    default:
      return 4;
  }
};

export const compareVersesByPosition = (a: { Verse: string; Flag?: string }, b: { Verse: string; Flag?: string }): number =>
  Number.parseInt(String(a.Verse), 10) - Number.parseInt(String(b.Verse), 10) ||
  getVerseFlagSortOrder(a.Flag) - getVerseFlagSortOrder(b.Flag);

export const getVerseFlagSpan = (flag?: string): { start: number; end: number } => {
  switch (String(flag || "0")) {
    case "1":
      return { start: 0, end: 1 };
    case "3":
      return { start: 1, end: 2 };
    case "2":
      return { start: 2, end: 3 };
    default:
      return { start: 0, end: 3 };
  }
};

export const doesVerseOverlapRange = (
  range: VerseRangeItem,
  chapter: number,
  verseNum: number,
  flag?: string
): boolean => {
  if (verseNum < range.verseStart || verseNum > range.verseEnd) return false;
  const span = getVerseFlagSpan(flag);
  let requestedStart = 0;
  let requestedEnd = 3;
  if (chapter === range.chapterStart && verseNum === range.verseStart && range.flagStart) {
    requestedStart = getVerseFlagSpan(range.flagStart).start;
  }
  if (chapter === range.chapterEnd && verseNum === range.verseEnd && range.flagEnd) {
    requestedEnd = getVerseFlagSpan(range.flagEnd).end;
  }
  return span.end > requestedStart && span.start < requestedEnd;
};

// 渲染经文文本，带内联注脚标记（holybible renderVerseText 移植）
export const renderVerseText = (
  verse: { Verse: string; Text: string; Flag?: string; mergedRecoveryParts?: { Flag?: string; Verse: string; Text: string }[] },
  footnotes: BibleFootnote[],
  books: BibleBook[],
  bookId: string,
  chapter: number,
  flag: string,
  keyPrefix: string,
  onOpenFootnote: (fn: FootnoteData) => void
): React.ReactNode => {
  const fns = verse.mergedRecoveryParts
    ? mergeRecoveryVerseFootnotes(verse.mergedRecoveryParts, footnotes, books, bookId, chapter)
    : getFootnotes(footnotes, books, bookId, chapter, verse.Verse, flag);
  if (fns.length === 0) return <span>{verse.Text}</span>;
  const text = verse.Text;
  const parts: React.ReactNode[] = [];
  let last = 0;
  fns.forEach((fn) => {
    const pos = fn.location - 1;
    if (pos > last) parts.push(<span key={`${keyPrefix}t-${fn._id}`}>{text.slice(last, pos)}</span>);
    parts.push(
      <sup
        key={`${keyPrefix}fn-${fn._id}`}
        className="footnote-num"
        onClick={(e) => {
          e.stopPropagation();
          onOpenFootnote(fn);
        }}
      >
        {fn.seq}
      </sup>
    );
    last = pos;
  });
  if (last < text.length) parts.push(<span key={`${keyPrefix}t-${last}`}>{text.slice(last)}</span>);
  return <span>{parts}</span>;
};


export const parseRefs = (
  text: string,
  ctx: RefParseContext
): React.ReactNode[] => {
  const { curBookId, curChapter, curVerse, curFlag, sourceFootnoteId, sourceLocation } = ctx;
    if (!ctx.books || ctx.books.length === 0) return [text];
    const books: BibleBook[] = ctx.books;
    const acronyms = books
      .filter((b: any) => b.acronym_name)
      .map((b: any) => ({ a: b.acronym_name, b }))
      .sort((x: any, y: any) => y.a.length - x.a.length);
    const fullNames = books
      .map((b: any) => ({ n: b.name, b }))
      .sort((x: any, y: any) => y.n.length - x.n.length);
    const cnNums = '一二三四五六七八九十百零○';
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const abbrPat = acronyms.map((x: any) => esc(x.a)).join('|');
    const fullNamePat = fullNames.map((x: any) => esc(x.n)).join('|');
    const referencePrefixRe = /^[参參]\s*/;
    const noteConnectorPattern = '[与與]';
    const noteMarkerPattern = '[注註]';
    const verseMarkerPattern = '[节節]';
    const noteJoinPattern = `(?:\\s*${noteConnectorPattern}\\s*)?`;
    const stripReferencePrefix = (value: string) => value.replace(referencePrefixRe, '');
    const getMatchedLabel = (matchText: string, prefixLength = 0) => matchText.slice(prefixLength);
    const parseVerseFlag = (value?: string): string | undefined => {
      if (value === '上') return '1';
      if (value === '下') return '2';
      if (value === '中') return '3';
      return undefined;
    };
    const parseRangeEnd = (value: string): { chapterEnd: number | null; verseEnd: number; flagEnd?: string } | null => {
      const singleVerseMatch = /^(\d+)([上下中])?$/.exec(value);
      if (singleVerseMatch) {
        return {
          chapterEnd: null,
          verseEnd: parseInt(singleVerseMatch[1]),
          flagEnd: parseVerseFlag(singleVerseMatch[2]),
        };
      }
      const match = new RegExp(`^([${cnNums}]+|\\d+)(\\d+)([上下中])?$`).exec(value);
      if (!match) return null;
      const chapterEnd = /^\\d+$/.test(match[1]) ? parseInt(match[1]) : chineseNumToInt(match[1]);
      if (chapterEnd === null) return null;
      return { chapterEnd, verseEnd: parseInt(match[2]), flagEnd: parseVerseFlag(match[3]) };
    };
    const parseVerseOnlyRangeEnd = (value: string): { verseEnd: number; flagEnd?: string } | null => {
      const match = /^(\d+)([上下中])?$/.exec(value);
      if (!match) return null;
      return {
        verseEnd: parseInt(match[1], 10),
        flagEnd: parseVerseFlag(match[2]),
      };
    };
    const verseOnlyRefRe = new RegExp(`^(\\d+)([上下中])?(?:[～~](\\d+[上下中]?))?(?:${verseMarkerPattern})?$`);
    const parseCurrentChapterVerseRanges = (
      segments: string[],
      chapter: number,
      book?: { b: any } | null,
    ): VerseRangeItem[] | null => {
      const ranges: VerseRangeItem[] = [];
      for (const seg of segments) {
        const trimmed = stripReferencePrefix(seg.trim());
        if (!trimmed) continue;
        const verseOnlyMatch = verseOnlyRefRe.exec(trimmed);
        if (!verseOnlyMatch) return null;
        const verseStart = parseInt(verseOnlyMatch[1], 10);
        const flagStart = parseVerseFlag(verseOnlyMatch[2]);
        let verseEnd = verseStart;
        let flagEnd = flagStart;
        if (verseOnlyMatch[3]) {
          const rangeEnd = parseVerseOnlyRangeEnd(verseOnlyMatch[3]);
          if (!rangeEnd) return null;
          verseEnd = rangeEnd.verseEnd;
          flagEnd = rangeEnd.flagEnd;
        }
        ranges.push({
          bookId: book?.b._id || curBookId,
          bookName: book?.b.name || ctx.getBookName(curBookId),
          chapterStart: chapter,
          verseStart,
          chapterEnd: chapter,
          verseEnd,
          flagStart,
          flagEnd,
        });
      }
      return ranges.length > 0 ? ranges : null;
    };

    interface RefMatch { index: number; end: number; bookId?: string; bookName?: string; chapter?: number; verse?: string; endVerse?: string; annSeq?: string | null; label: string; isRange?: boolean; ranges?: VerseRangeItem[]; explicitBook?: boolean; groupedEntries?: RefMatch[]; }
    const matches: RefMatch[] = [];
    const compoundSegmentLeadRe = new RegExp(`^(?:${fullNamePat}|${abbrPat}|[${cnNums}]+\\d+|\\d)`);
    const splitCompoundReferenceSegment = (value: string): string[] => {
      const parts: string[] = [];
      let start = 0;
      for (let i = 0; i < value.length; i += 1) {
        const ch = value[i];
        if (ch !== '与' && ch !== '與') continue;
        const before = value.slice(start, i).trimEnd();
        const after = value.slice(i + 1).trimStart();
        if (!before || !after) continue;
        if (!/[\d上下中]$/.test(before)) continue;
        if (/^[注註]/.test(after)) continue;
        if (!compoundSegmentLeadRe.test(after)) continue;
        const segment = before.trim();
        if (segment) parts.push(segment);
        start = i + 1;
      }
      const tail = value.slice(start).trim();
      if (tail) parts.push(tail);
      return parts;
    };
    const refReWithCh = new RegExp(`^([${cnNums}]+|\\d+)(\\d+)([上下中])?(?:[～~]([${cnNums}\\d]+[上下中]?))?$`);
    const trailingAnnotationBodyPattern = `(?:${noteMarkerPattern}(\\d+)|(\\d+)${noteMarkerPattern}(\\d+))`;
    const optionalTrailingAnnotationPattern = `(?:${noteJoinPattern}${trailingAnnotationBodyPattern})?`;
    const chapterVerseAnnRe = new RegExp(`^([${cnNums}]+)(\\d+)(?:${verseMarkerPattern})?(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))$`);
    const verseOnlyAnnRe = new RegExp(`^(\\d+)(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))$`);
    const resolveTrailingAnnotation = (
      startVerse: string,
      endVerse: string,
      directSeq?: string | null,
      explicitVerse?: string | null,
      explicitSeq?: string | null,
    ) => {
      if (explicitVerse && explicitSeq) {
        return { annSeq: explicitSeq, annVerse: explicitVerse, annEndVerse: explicitVerse };
      }
      return { annSeq: directSeq || null, annVerse: startVerse, annEndVerse: endVerse };
    };
    const splitParentheticalGroups = (value: string): { text: string; start: number; end: number }[] => {
      const groups: { text: string; start: number; end: number }[] = [];
      let start = 0;
      for (let i = 0; i < value.length; i += 1) {
        if (!/[，,、]/.test(value[i])) continue;
        groups.push({ text: value.slice(start, i), start, end: i });
        start = i + 1;
      }
      groups.push({ text: value.slice(start), start, end: value.length });
      return groups;
    };
    const parseParentheticalGroup = (
      value: string,
      initialBook: any = null,
      initialChapter: number | null = null,
    ): {
      ranges: VerseRangeItem[];
      annRefData: { bookId: string; bookName: string; chapter: number; verse: string; endVerse?: string; seq: string } | null;
      hasExplicitBook: boolean;
      nextBook: any;
      nextChapter: number | null;
    } => {
      const ranges: VerseRangeItem[] = [];
      let annRefData: { bookId: string; bookName: string; chapter: number; verse: string; endVerse?: string; seq: string } | null = null;
      const segs = splitCompoundReferenceSegment(value)
        .flatMap(seg => seg.split(/[，,、\s]+/g))
        .map(seg => seg.trim())
        .filter(Boolean);
      let parenCtxBook: any = initialBook;
      let parenCtxChapter: number | null = initialChapter;
      let hasExplicitBook = false;
      for (const seg of segs) {
        const trimmed = seg.trim();
        const normalized = stripReferencePrefix(trimmed);
        if (!normalized || /^[与與]$/.test(normalized)) continue;
        let book: any = null;
        let refPart = normalized;
        for (const fn of fullNames) {
          if (normalized.startsWith(fn.n)) { book = fn; refPart = normalized.slice(fn.n.length); break; }
        }
        if (!book) {
          for (const ac of acronyms) {
            if (normalized.startsWith(ac.a)) { book = ac; refPart = normalized.slice(ac.a.length); break; }
          }
        }

        const chapterVerseAnnMatch = chapterVerseAnnRe.exec(refPart);
        if (chapterVerseAnnMatch?.[3]) {
          const chapterValue = chapterVerseAnnMatch[1];
          const parsedChapter = /^\d+$/.test(chapterValue) ? parseInt(chapterValue, 10) : chineseNumToInt(chapterValue);
          if (parsedChapter !== null) {
            const annBookId = book?.b._id || parenCtxBook?.b._id || curBookId;
            const annBookName = book?.b.name || parenCtxBook?.b.name || ctx.getBookName(curBookId);
            annRefData = {
              bookId: annBookId,
              bookName: annBookName,
              chapter: parsedChapter,
              verse: chapterVerseAnnMatch[2],
              endVerse: chapterVerseAnnMatch[2],
              seq: chapterVerseAnnMatch[3],
            };
            if (book) {
              hasExplicitBook = true;
              parenCtxBook = book;
            }
            parenCtxChapter = parsedChapter;
            continue;
          }
        }

        const verseOnlyAnnMatch = verseOnlyAnnRe.exec(refPart);
        if (verseOnlyAnnMatch?.[2]) {
          const annChapter = parenCtxChapter ?? curChapter;
          const annBookId = book?.b._id || parenCtxBook?.b._id || curBookId;
          const annBookName = book?.b.name || parenCtxBook?.b.name || ctx.getBookName(curBookId);
          annRefData = {
            bookId: annBookId,
            bookName: annBookName,
            chapter: annChapter,
            verse: verseOnlyAnnMatch[1],
            endVerse: verseOnlyAnnMatch[1],
            seq: verseOnlyAnnMatch[2],
          };
          if (book) {
            hasExplicitBook = true;
            parenCtxBook = book;
          }
          continue;
        }

        const verseOnlyMatch = !book ? verseOnlyRefRe.exec(refPart) : null;
        if (verseOnlyMatch) {
          const verseStart = parseInt(verseOnlyMatch[1], 10);
          const flagStart = parseVerseFlag(verseOnlyMatch[2]);
          let verseEnd = verseStart;
          let flagEnd = flagStart;
          if (verseOnlyMatch[3]) {
            const rangeEnd = parseVerseOnlyRangeEnd(verseOnlyMatch[3]);
            if (!rangeEnd) continue;
            verseEnd = rangeEnd.verseEnd;
            flagEnd = rangeEnd.flagEnd;
          }
          const targetChapter = parenCtxChapter ?? curChapter;
          const targetBookId = parenCtxBook?.b._id || curBookId;
          const targetBookName = parenCtxBook?.b.name || ctx.getBookName(curBookId);
          ranges.push({ bookId: targetBookId, bookName: targetBookName, chapterStart: targetChapter, verseStart, chapterEnd: targetChapter, verseEnd, flagStart, flagEnd });
          continue;
        }

        const sm = refReWithCh.exec(refPart);
        if (!sm) continue;
        const chStart = /^\d+$/.test(sm[1]) ? parseInt(sm[1], 10) : chineseNumToInt(sm[1]);
        if (chStart === null) continue;
        const vStart = parseInt(sm[2], 10);
        const flagStart = parseVerseFlag(sm[3]);
        let chEnd = chStart;
        let vEnd = vStart;
        let flagEnd = flagStart;
        if (sm[4]) {
          const rangeEnd = parseRangeEnd(sm[4]);
          if (!rangeEnd) continue;
          if (rangeEnd.chapterEnd !== null) chEnd = rangeEnd.chapterEnd;
          vEnd = rangeEnd.verseEnd;
          flagEnd = rangeEnd.flagEnd;
        }
        if (book) {
          hasExplicitBook = true;
          parenCtxBook = book;
          parenCtxChapter = chStart;
          ranges.push({ bookId: book.b._id, bookName: book.b.name, chapterStart: chStart, verseStart: vStart, chapterEnd: chEnd, verseEnd: vEnd, flagStart, flagEnd });
        } else if (parenCtxBook) {
          parenCtxChapter = chStart;
          ranges.push({ bookId: parenCtxBook.b._id, bookName: parenCtxBook.b.name, chapterStart: chStart, verseStart: vStart, chapterEnd: chEnd, verseEnd: vEnd, flagStart, flagEnd });
        } else {
          parenCtxChapter = chStart;
          ranges.push({ bookId: curBookId, bookName: ctx.getBookName(curBookId), chapterStart: chStart, verseStart: vStart, chapterEnd: chEnd, verseEnd: vEnd, flagStart, flagEnd });
        }
      }
      return { ranges, annRefData, hasExplicitBook, nextBook: parenCtxBook, nextChapter: parenCtxChapter };
    };

    // 模式0: 括号括起的复合注解引用与经文范围引用（全角/半角括号均支持）
    // 优先识别整段括号注解引用，如（启二二16与注4）
    const parenRe = new RegExp(`([（(])([^）)]*(?:${fullNamePat}|${abbrPat}|[${cnNums}]+\\d+|\\d+~\\d+|\\d+～\\d+)[^）)]*)([）)])`, 'g');
    const innerAnnRe = new RegExp(`^([${cnNums}]+)(\\d+)(?:${verseMarkerPattern})?(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))$`);
    const currentChapterListParenRe = new RegExp(`([（(])((?:[参參]\\s*)?\\d+(?:\\s*[，,、]\\s*\\d+)*)([）)])`, 'g');
    for (let mpList: RegExpExecArray | null; (mpList = currentChapterListParenRe.exec(text)) !== null; ) {
      const listSegments = stripReferencePrefix(mpList[2].trim()).split(/[，,、]/g);
      const listRanges = parseCurrentChapterVerseRanges(listSegments, curChapter, null);
      if (!listRanges) continue;
      matches.push({
        index: mpList.index,
        end: mpList.index + mpList[0].length,
        isRange: true,
        label: mpList[0],
        explicitBook: false,
        ranges: listRanges,
      });
    }
    for (let mp: RegExpExecArray | null; (mp = parenRe.exec(text)) !== null; ) {
      const inner = mp[2];
      const normalizedInner = stripReferencePrefix(inner.trim());
      let annBook: any = null;
      let annRefPart = normalizedInner;
      for (const fn of fullNames) {
        if (normalizedInner.startsWith(fn.n)) { annBook = fn; annRefPart = normalizedInner.slice(fn.n.length); break; }
      }
      if (!annBook) {
        for (const ac of acronyms) {
          if (normalizedInner.startsWith(ac.a)) { annBook = ac; annRefPart = normalizedInner.slice(ac.a.length); break; }
        }
      }
      const innerAnnMatch = innerAnnRe.exec(annRefPart);
      if (innerAnnMatch?.[3]) {
        const ch = chineseNumToInt(innerAnnMatch[1]);
        if (ch !== null) {
          matches.push({ index: mp.index, end: mp.index + mp[0].length, bookId: annBook?.b._id, bookName: annBook?.b.name, chapter: ch, verse: innerAnnMatch[2], annSeq: innerAnnMatch[3], label: mp[0], explicitBook: !!annBook });
          continue;
        }
      }

      let parenCtxBook: any = null;
      let parenCtxChapter: number | null = null;
      const groupedEntries: RefMatch[] = [];
      for (const group of splitParentheticalGroups(inner)) {
        const rawGroup = group.text;
        const trimmedGroup = rawGroup.trim();
        if (!trimmedGroup) continue;

        const prefixLength = trimmedGroup.match(referencePrefixRe)?.[0].length || 0;
        const leadingWhitespace = rawGroup.length - rawGroup.trimStart().length;
        const trailingWhitespace = rawGroup.length - rawGroup.trimEnd().length;
        const groupIndex = mp.index + mp[1].length + group.start + leadingWhitespace + prefixLength;
        const groupEnd = mp.index + mp[1].length + group.end - trailingWhitespace;
        const label = getMatchedLabel(trimmedGroup, prefixLength);
        if (!label || groupEnd <= groupIndex) continue;

        const { ranges, annRefData, hasExplicitBook, nextBook, nextChapter } = parseParentheticalGroup(trimmedGroup, parenCtxBook, parenCtxChapter);
        parenCtxBook = nextBook;
        parenCtxChapter = nextChapter;

        if (ranges.length > 0) {
          groupedEntries.push({
            index: groupIndex,
            end: groupEnd,
            bookId: annRefData?.bookId,
            bookName: annRefData?.bookName,
            chapter: annRefData?.chapter,
            verse: annRefData?.verse,
            endVerse: annRefData?.endVerse,
            annSeq: annRefData?.seq,
            isRange: true,
            label,
            explicitBook: hasExplicitBook,
            ranges,
          });
          continue;
        }

        if (annRefData) {
          groupedEntries.push({
            index: groupIndex,
            end: groupEnd,
            bookId: annRefData.bookId,
            bookName: annRefData.bookName,
            chapter: annRefData.chapter,
            verse: annRefData.verse,
            endVerse: annRefData.endVerse,
            annSeq: annRefData.seq,
            label,
            explicitBook: hasExplicitBook,
          });
        }
      }

      if (groupedEntries.length === 0) continue;
      matches.push({
        index: mp.index,
        end: mp.index + mp[0].length,
        label: mp[0],
        explicitBook: groupedEntries.some(entry => !!entry.explicitBook),
        groupedEntries,
      });
    }

    // 模式1: [参/參]? 书卷缩写 中文章节 阿拉伯节号 [～阿拉伯结束节号]? [节/節]? [[与/與]注/註N]?
    const re1 = new RegExp(`([参參]\\s*)?(${abbrPat})([${cnNums}]+)(\\d+)(?:[～~](\\d+))?(${verseMarkerPattern})?${optionalTrailingAnnotationPattern}`, 'g');
    for (let m1: RegExpExecArray | null; (m1 = re1.exec(text)) !== null; ) {
      const ac = acronyms.find((x: any) => x.a === m1[2]);
      if (!ac) continue;
      const ch = chineseNumToInt(m1[3]);
      if (ch === null) continue;
      const rangeEndVerse = m1[5] || m1[4];
      const { annSeq, annVerse, annEndVerse } = resolveTrailingAnnotation(m1[4], rangeEndVerse, m1[7], m1[8], m1[9]);
      const label = getMatchedLabel(m1[0], m1[1]?.length || 0);
      matches.push({ index: m1.index + (m1[1]?.length || 0), end: m1.index + m1[0].length, bookId: ac.b._id, bookName: ac.b.name, chapter: ch, verse: annSeq ? annVerse : m1[4], endVerse: annSeq ? annEndVerse : rangeEndVerse, annSeq, label, explicitBook: true, isRange: !!m1[5], ranges: m1[5] ? [{ bookId: ac.b._id, bookName: ac.b.name, chapterStart: ch, verseStart: parseInt(m1[4]), chapterEnd: ch, verseEnd: parseInt(m1[5]) }] : undefined });
    }

    // 模式2: [参/參]? 中文章节 阿拉伯节号 [～阿拉伯结束节号]? [节/節]? [[与/與]注/註N]?（无书卷缩写，继承上下文）
    const re2 = new RegExp(`([参參]\\s*)?([${cnNums}]+)(\\d+)(?:[～~](\\d+))?(${verseMarkerPattern})?${optionalTrailingAnnotationPattern}`, 'g');
    for (let m2: RegExpExecArray | null; (m2 = re2.exec(text)) !== null; ) {
      const ch = chineseNumToInt(m2[2]);
      if (ch === null) continue;
      const rangeEndVerse = m2[4] || m2[3];
      const { annSeq, annVerse, annEndVerse } = resolveTrailingAnnotation(m2[3], rangeEndVerse, m2[6], m2[7], m2[8]);
      const label = getMatchedLabel(m2[0], m2[1]?.length || 0);
      matches.push({ index: m2.index + (m2[1]?.length || 0), end: m2.index + m2[0].length, bookId: curBookId, bookName: ctx.getBookName(curBookId), chapter: ch, verse: annSeq ? annVerse : m2[3], endVerse: annSeq ? annEndVerse : rangeEndVerse, annSeq, label, explicitBook: false, isRange: !!m2[4], ranges: m2[4] ? [{ bookId: curBookId, bookName: ctx.getBookName(curBookId), chapterStart: ch, verseStart: parseInt(m2[3]), chapterEnd: ch, verseEnd: parseInt(m2[4]) }] : undefined });
    }

    // 模式3a: 阿拉伯节号～阿拉伯节号 + 节/節（纯节号范围）
    const re3a = new RegExp(`(\\d+)[～~](\\d+)(${verseMarkerPattern})`, 'g');
    for (let m3a: RegExpExecArray | null; (m3a = re3a.exec(text)) !== null; ) {
      const vStart = parseInt(m3a[1]);
      const vEnd = parseInt(m3a[2]);
      const label = m3a[0];
      matches.push({ index: m3a.index, end: m3a.index + m3a[0].length, bookId: curBookId, bookName: ctx.getBookName(curBookId), chapter: curChapter, verse: m3a[1], endVerse: m3a[2], label, explicitBook: false, isRange: true, ranges: [{ bookId: curBookId, bookName: ctx.getBookName(curBookId), chapterStart: curChapter, verseStart: vStart, chapterEnd: curChapter, verseEnd: vEnd }] });
    }

    // 模式3b: 阿拉伯节号 + 节/節 [[与/與]注/註N]?（继承上下文书卷及章）
    const re3b = new RegExp(`(\\d+)(${verseMarkerPattern})(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))?`, 'g');
    for (let m3b: RegExpExecArray | null; (m3b = re3b.exec(text)) !== null; ) {
      const annSeq = m3b[3] || null;
      const label = m3b[0];
      matches.push({ index: m3b.index, end: m3b.index + m3b[0].length, verse: m3b[1], endVerse: m3b[1], annSeq, label, explicitBook: false });
    }

    // 模式3c: 阿拉伯节号 [[与/與]注/註N]（继承上下文书卷及章）
    const re3c = new RegExp(`(\\d+)(?:${noteJoinPattern}${noteMarkerPattern}(\\d+))`, 'g');
    for (let m3c: RegExpExecArray | null; (m3c = re3c.exec(text)) !== null; ) {
      const annSeq = m3c[2] || null;
      if (!annSeq) continue;
      const label = m3c[0];
      matches.push({ index: m3c.index, end: m3c.index + m3c[0].length, verse: m3c[1], endVerse: m3c[1], annSeq, label, explicitBook: false });
    }

    // 扩展匹配：将 、-分隔的后续节号并入前面的引用
    // 例如 "约一14、17" → 整体作为一个引用，ranges 含 1:14 和 1:17
    const verseOnlyWithFlag = /^[、，,](\d+)([上下中])?/;
    for (const mt of matches) {
      if (!mt.bookId && mt.chapter === undefined && !mt.ranges) continue;
      let tail = text.slice(mt.end);
      let extended = false;
      while (verseOnlyWithFlag.test(tail)) {
        const m = verseOnlyWithFlag.exec(tail)!;
        const v = m[1];
        const f = m[2];
        mt.end += m[0].length;
        mt.label = text.slice(mt.index, mt.end);

        if (!mt.ranges) {
          const rc = mt.chapter ?? curChapter;
          const rb = mt.bookId ?? curBookId;
          const rn = mt.bookName ?? ctx.getBookName(curBookId);
          mt.ranges = [{
            bookId: rb, bookName: rn,
            chapterStart: rc, verseStart: parseInt(mt.verse || '1'),
            chapterEnd: rc, verseEnd: parseInt(mt.endVerse || mt.verse || '1'),
          }];
        }

        const rc = mt.chapter ?? curChapter;
        const rb = mt.bookId ?? curBookId;
        const rn = mt.bookName ?? ctx.getBookName(curBookId);
        mt.ranges.push({
          bookId: rb, bookName: rn,
          chapterStart: rc, verseStart: parseInt(v),
          chapterEnd: rc, verseEnd: parseInt(v),
          flagStart: parseVerseFlag(f), flagEnd: parseVerseFlag(f),
        });
        mt.isRange = true;
        extended = true;
        tail = text.slice(mt.end);
      }
      // 如果有扩展，更新 endVerse 为最后一个节号
      if (extended && mt.ranges && mt.ranges.length > 0) {
        const last = mt.ranges[mt.ranges.length - 1];
        mt.endVerse = String(last.verseEnd);
      }
    }

    // 按位置排序，同位置优先长匹配；去除重叠
    matches.sort((a, b) => a.index - b.index || b.end - a.end);
    const kept: RefMatch[] = [];
    let prevEnd = 0;
    for (const mt of matches) {
      if (mt.index < prevEnd) continue;
      kept.push(mt);
      prevEnd = mt.end;
    }

    if (kept.length === 0) return [text];

    // 按出现顺序解析上下文书卷：遇到显式书卷时更新上下文，否则继承
    let contextBookId = curBookId;
    let contextBookName = ctx.getBookName(curBookId);
    let contextChapter = curChapter;
    const resolved: { mt: RefMatch; bookId: string; bookName: string; chapter: number }[] = [];
    for (const mt of kept) {
      if (mt.isRange) {
        // 括号范围引用：有显式书卷则更新上下文，否则继承
        if (mt.ranges && mt.ranges.length > 0 && mt.ranges[0].bookId) {
          contextBookId = mt.ranges[0].bookId;
          contextBookName = mt.ranges[0].bookName;
        }
        const resolvedChapter = mt.ranges?.[0]?.chapterStart ?? contextChapter;
        const lastRange = mt.ranges?.[mt.ranges.length - 1];
        if (lastRange) contextChapter = lastRange.chapterEnd;
        resolved.push({ mt, bookId: contextBookId, bookName: contextBookName, chapter: resolvedChapter });
        continue;
      }
      const resolvedChapter = mt.chapter ?? contextChapter;
      if (mt.explicitBook) {
        contextBookId = mt.bookId!;
        contextBookName = mt.bookName!;
        if (mt.chapter !== undefined) contextChapter = mt.chapter;
        resolved.push({ mt, bookId: mt.bookId!, bookName: mt.bookName!, chapter: resolvedChapter });
      } else {
        if (mt.chapter !== undefined) contextChapter = mt.chapter;
        resolved.push({ mt, bookId: contextBookId, bookName: contextBookName, chapter: resolvedChapter });
      }
    }

    const parts: React.ReactNode[] = [];
    let last = 0;
    for (const { mt, bookId, chapter } of resolved) {
      if (mt.index > last) parts.push(text.slice(last, mt.index));
      parts.push(
        <span
          key={`ref-${mt.index}`}
          className="ref-link"
          onClick={(e) => {
            e.stopPropagation();
            ctx.nextModalId.current += 1;
            const annBookId = mt.bookId || bookId;
            const annChapter = mt.chapter ?? chapter;
            if (mt.groupedEntries && mt.groupedEntries.length > 0) {
              const groupedParenEntries = mt.groupedEntries
                .map((entry, entryIndex): GroupedParenEntry | null => {
                  const entryBookId = entry.bookId || bookId;
                  const entryChapter = entry.chapter ?? chapter;

                  if (entry.isRange && entry.ranges && entry.ranges.length > 0) {
                    const rangeBookId = entryBookId || entry.ranges[0].bookId;
                    if (!rangeBookId) return null;
                    const rangeAnnBookId = entry.bookId || rangeBookId;
                    const rangeAnnChapter = entry.chapter ?? entryChapter;
                    return {
                      id: `group-${ctx.nextModalId.current}-${entryIndex}`,
                      label: entry.label,
                      kind: 'range',
                      bookId: rangeBookId,
                      verseRanges: entry.ranges,
                      annRef: entry.annSeq && rangeAnnChapter !== undefined ? {
                        bookId: rangeAnnBookId,
                        chapter: rangeAnnChapter,
                        verse: entry.verse!,
                        endVerse: entry.endVerse,
                        seq: entry.annSeq,
                        flag: rangeAnnBookId === curBookId && rangeAnnChapter === curChapter && String(entry.verse) === String(curVerse) ? curFlag : undefined,
                        sourceFootnoteId,
                        sourceLocation,
                        label: entry.label,
                      } : undefined,
                    };
                  }

                  if (entry.annSeq && entryBookId && entryChapter !== undefined) {
                    const shouldPreferCurrentFlag = entryBookId === curBookId && entryChapter === curChapter && String(entry.verse) === String(curVerse);
                    return {
                      id: `group-${ctx.nextModalId.current}-${entryIndex}`,
                      label: entry.label,
                      kind: 'ann',
                      annRef: {
                        bookId: entryBookId,
                        chapter: entryChapter,
                        verse: entry.verse!,
                        endVerse: entry.endVerse,
                        seq: entry.annSeq,
                        flag: shouldPreferCurrentFlag ? curFlag : undefined,
                        sourceFootnoteId,
                        sourceLocation,
                        label: entry.label,
                      },
                    };
                  }

                  if (entryBookId && entryChapter !== undefined && entry.verse) {
                    return {
                      id: `group-${ctx.nextModalId.current}-${entryIndex}`,
                      label: entry.label,
                      kind: 'verse',
                      bookId: entryBookId,
                      chapter: entryChapter,
                      verse: entry.verse,
                    };
                  }

                  return null;
                })
                .filter((entry): entry is GroupedParenEntry => Boolean(entry));

              if (groupedParenEntries.length === 0) return;

              const firstGroupedEntry = groupedParenEntries[0];
              ctx.pushModal({
                id: `grouped-${ctx.nextModalId.current}`,
                bookId: firstGroupedEntry.kind === 'ann' ? firstGroupedEntry.annRef.bookId : firstGroupedEntry.bookId,
                groupedParenEntries,
                groupedParenLabel: mt.label,
              });
            } else if (mt.isRange && mt.ranges) {
              ctx.pushModal({
                id: `range-${ctx.nextModalId.current}`,
                bookId: bookId || mt.ranges[0].bookId,
                verseRanges: mt.ranges,
                rangeLabel: mt.label,
                annRef: mt.annSeq ? {
                  bookId: annBookId,
                  chapter: annChapter,
                  verse: mt.verse!,
                  endVerse: mt.endVerse,
                  seq: mt.annSeq,
                  flag: annBookId === curBookId && annChapter === curChapter && String(mt.verse) === String(curVerse) ? curFlag : undefined,
                  sourceFootnoteId,
                  sourceLocation,
                  label: mt.label,
                } : undefined,
              });
            } else if (mt.annSeq) {
              const shouldPreferCurrentFlag = annBookId === curBookId && annChapter === curChapter && String(mt.verse) === String(curVerse);
              ctx.pushModal({
                id: `ann-${ctx.nextModalId.current}`,
                bookId: annBookId,
                chapter: annChapter,
                annRef: {
                  bookId: annBookId,
                  chapter: annChapter,
                  verse: mt.verse!,
                  endVerse: mt.endVerse,
                  seq: mt.annSeq,
                  flag: shouldPreferCurrentFlag ? curFlag : undefined,
                  sourceFootnoteId,
                  sourceLocation,
                  label: mt.label,
                },
              });
            } else {
              ctx.pushModal({
                id: `verse-${ctx.nextModalId.current}`,
                bookId,
                chapter,
                verseRef: { bookId, chapter, verse: mt.verse! },
              });
            }
          }}
        >
          {mt.label}
        </span>
      );
      last = mt.end;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };
